import { FORM_ERROR } from 'final-form';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';

import { AppError } from '@vk-hr-tek/core/error';
import {
  pollDownloadStatus,
  setDownloadWaiting,
} from '@vk-hr-tek/core/download-notification';

import { ThunkExtra } from '@app/store';
import { ContentItem, ContentSurveyItem } from '@app/gen/community';
import {
  ContentService,
  ContentSurveyAnswerParams,
  ContentSurveyAnswerRequest,
  CreateContentSurveyReportParams,
} from '@app/gen/content';

import { ThunkValuesAndActions } from '../../../app/store';

export const startDownloadSurveyReport = createAsyncThunk<
  void,
  CreateContentSurveyReportParams,
  ThunkExtra
>(
  'content-surveys/startDownloadSurveyReport',
  async (params, { dispatch, rejectWithValue, extra: { inject } }) => {
    try {
      const contentService = inject<ContentService>(ContentService);

      const report = await contentService.createContentSurveyReport({
        params,
      });

      await dispatch(
        setDownloadWaiting({
          id: report.download_id,
          count: 1,
          text: {
            loading: 'Создаем файл (1 документ)',
            complete: 'Файл создан (1 документ)',
            failed: 'Ошибка создания файла',
          },
        }),
      );

      dispatch(pollDownloadStatus(report));
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const createSurveyAnswersAsyncThunk = (type: string) =>
  createAsyncThunk<
    ContentSurveyItem,
    ThunkValuesAndActions<{
      entityId: string;
      params: ContentSurveyAnswerParams;
      body: ContentSurveyAnswerRequest;
    }>,
    ThunkExtra
  >(
    type,
    async (
      { values: { params, body }, actions },
      { rejectWithValue, extra: { inject } },
    ) => {
      try {
        const contentService = inject<ContentService>(ContentService);

        const result = await contentService.contentSurveyAnswer({
          params,
          body,
        });

        actions.resolve(null);

        return result;
      } catch (err) {
        actions.reject({ [FORM_ERROR]: err });

        return rejectWithValue(classToPlain(err) as AppError);
      }
    },
  );

interface EntitySurvey {
  id: string;
  content?: ContentItem;
}

export const setContentSurveyAnswers = <
  T extends EntitySurvey | EntitySurvey[],
>(
  entities: T,
  entityId: string,
  surveyId: string,
  updatedSurvey: ContentSurveyItem,
): T => {
  const getSurveysWithAnswers = (entity: EntitySurvey) => {
    return entity.content?.surveys?.map((survey) => {
      if (survey.survey.id === surveyId) {
        return updatedSurvey;
      }

      return survey;
    });
  };

  if (Array.isArray(entities)) {
    return entities.map((entity) => {
      if (entity.id === entityId) {
        return {
          ...entity,
          content: {
            ...entity.content,
            surveys: getSurveysWithAnswers(entity),
          },
        };
      }

      return entity;
    }) as T;
  }

  return {
    ...entities,
    content: {
      ...entities.content,
      surveys: getSurveysWithAnswers(entities),
    },
  };
};
